import { Button, Flex, MultiSortRef, Search, Text } from "@theorgchartdev/storybook";
import { getFieldsFromConfig, PROFILE_TEST_PREFIX } from "./ProfileConfiguration.utils";
import { ProfileTypes } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/ProfileBaseballCard.utils";
import { FieldMultiSort } from "@/appComponents/Blocks/Composites/FieldMultiSort/FieldMultiSort";
import { appstr } from "@coreapp/utils/appstr";
import { useMemo, useRef, useState } from "react";
import { filterInternalFields } from "@/Utils/FilterInternalFields";

export type ProfileHeadingFieldProps = {
  type: ProfileTypes;
  fields: string[];
  config: any;
  handleUpdate: (config: any, fieldChanged: string) => void;
};

export const ProfileFieldsConfig = ({
  type,
  fields,
  config,
  handleUpdate
}: ProfileHeadingFieldProps) => {
  const [queryString, setQueryString] = useState("");
  const [_, setSelectedIds] = useState<string[]>([]);
  const multiSortRef = useRef<MultiSortRef>(null);

  const fieldsObject = useMemo(() => {
    const fieldSet = new Set<string>(fields);
    let defaultIncludedFields: string[] = config?.profile?.shownFields?.[type]?.Included || [];

    if (Array.isArray(config?.profile?.shownFields)) {
      // Legacy handling
      const legacyIncludedFields = config?.profile?.shownFields?.filter((f: string) =>
        fieldSet.has(f)
      );
      defaultIncludedFields.push(...legacyIncludedFields);
    } else if (config?.profile?.shownFields?.[type]?.Included == null) {
      // Default case
      defaultIncludedFields.push(...fields);
    }

    defaultIncludedFields = filterInternalFields(defaultIncludedFields);
    let defaultIncludedFieldsSet = new Set(defaultIncludedFields);
    let defaultExcludedFields = fields.filter((f: string) => !defaultIncludedFieldsSet.has(f));

    // Remove fields used in heading cards or that don't exist
    const fieldsToHide = new Set<string>(getFieldsFromConfig(config, type));
    defaultIncludedFields = defaultIncludedFields.filter(
      (field: string) => !fieldsToHide.has(field) && fieldSet.has(field)
    );
    defaultExcludedFields = defaultExcludedFields.filter(
      (field: string) => !fieldsToHide.has(field)
    ); // excluded are created from fields, so no need to check inclusion

    return { Excluded: defaultExcludedFields, Included: defaultIncludedFields };
  }, [config, fields, type]);

  const filteredFieldObjects = useMemo(() => {
    const isFiltered = queryString.length > 0;
    const filteredFieldsObj: { [key: string]: string[] } = { Excluded: [], Included: [] };

    if (!isFiltered) {
      return fieldsObject;
    }

    // Filter fields based on search query
    Object.keys(fieldsObject).forEach((key: string) => {
      filteredFieldsObj[key] = fieldsObject[key as "Included" | "Excluded"].filter((f: string) => {
        return f.toLowerCase().includes(queryString.toLowerCase());
      });
    });

    return filteredFieldsObj;
  }, [fieldsObject, queryString]);

  const update = (nextState: typeof fieldsObject) => {
    const newProfile = config?.profile || {};
    let nextProfileMap = structuredClone(newProfile);
    if (nextProfileMap?.shownFields?.[type] == null) {
      nextProfileMap.shownFields = {
        ...(Array.isArray(nextProfileMap.shownFields) ? {} : nextProfileMap.shownFields),
        [type]: { Included: [] }
      };
    }

    // Fields used in heading config must always be included so they appear in FieldToggleMenu.
    // These fields are hidden from the UI, so we need to add them to the Included list.
    const headingFields = getFieldsFromConfig(config, type);
    const includedWithHeadingFields = [...nextState.Included];
    for (const field of headingFields) {
      if (!includedWithHeadingFields.includes(field)) {
        includedWithHeadingFields.push(field);
      }
    }

    nextProfileMap.shownFields[type].Included = includedWithHeadingFields;
    handleUpdate({ profile: nextProfileMap }, "profile");
  };

  const onChange = (dispatch: any) => {
    const nextState = typeof dispatch === "function" ? dispatch(fieldsObject) : dispatch;
    update(nextState);
  };

  const onToggleAll = (target: "Included" | "Excluded") => () => {
    const nextState = {
      Included: [],
      Excluded: [],
      [target]: structuredClone(fieldsObject["Excluded"].concat(fieldsObject["Included"])) ?? []
    };
    update(nextState);
  };

  return (
    <Flex direction="column" gap="16px">
      <Flex direction="column" gap="4px">
        <Text modifier="bold">{appstr("Additional {type} information", { type })}</Text>
        <Text size="small">{appstr("Additional {type} information desc", { type })}</Text>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" gap="medium">
        <Flex justifyContent="flex-start" alignItems="center" flex="1" gap="8px">
          <Text modifier="bold" size="small">
            {appstr("Fields to")}:
          </Text>
          <Button
            modifier="secondary"
            disabled={!multiSortRef.current?.isSelectingInCategory("Included")}
            onClick={() => multiSortRef.current?.moveTo("Included", "Excluded")}
            dataTestId={`${PROFILE_TEST_PREFIX}-${type}-fields-exclude-button`}
          >
            {appstr("Exclude")}
          </Button>
          <Button
            modifier="secondary"
            disabled={!multiSortRef.current?.isSelectingInCategory("Excluded")}
            onClick={() => multiSortRef.current?.moveTo("Excluded", "Included")}
            dataTestId={`${PROFILE_TEST_PREFIX}-${type}-fields-include-button`}
          >
            {appstr("Include")}
          </Button>
        </Flex>
        <Flex flex="1" className="oc-profile-configuration-search-container">
          <Search
            placeholder={appstr("findfield")}
            onInput={(value: string) => setQueryString(value)}
          />
        </Flex>
      </Flex>
      <FieldMultiSort
        dataTestIdPrefix={`${PROFILE_TEST_PREFIX}-${type}-fields`}
        hasFilter={queryString.length > 0}
        onChange={onChange}
        onToggleAll={onToggleAll}
        filteredItems={filteredFieldObjects as any}
        items={fieldsObject}
        headerMap={{
          Excluded: appstr("Exclude"),
          Included: appstr("Include")
        }}
        handleRef={multiSortRef}
        onSelectionChange={setSelectedIds}
        hideFieldCount
      />
    </Flex>
  );
};
